import styled from 'styled-components';

import Button from 'components/shared/Button';
import { colors } from 'styles/theme/colors';
import { BodySmall, BodySmallBold } from 'styles/typography';
import ChatBubbleIcon from 'svg/ChatBubbleIcon';

export type CostTierEmptyStateProps = {
  onCancel: () => void;
};

// A list whose medications carry no tiers still opens the cost tier form: the key cannot be authored
// from this screen, so an empty key is not something a user can fix here — the screen has to say so
// rather than be hidden. The message centres in the panel body and the actions sit at its foot, so the
// state reads as a considered screen rather than a form that failed to render its rows.
export default function CostTierEmptyState({ onCancel }: CostTierEmptyStateProps) {
  return (
    <Container>
      <Message>
        {/* The same glyph the Chat empty state uses, at its natural size, so the two read as one
            family rather than as two designers' takes on "nothing here yet". */}
        <Icon aria-hidden>
          <ChatBubbleIcon />
        </Icon>
        <Title>No Cost Tiers</Title>
        <Description>
          Cost tiers group medications by relative cost, assigned per medication as you add them.
        </Description>
      </Message>

      {/* Both actions close the form, because there is nothing here to save. Done is the one a user
          who has read the message reaches for; Cancel is the one they reach for having opened this
          by mistake. Neither is a no-op — leaving is the only thing either button can mean, whether
          the form replaced the admin panel's table or opened as the customer page's modal. */}
      <Actions>
        <Button variant='ghost' onClick={onCancel}>
          Cancel
        </Button>
        <Button variant='primary' onClick={onCancel}>
          Done
        </Button>
      </Actions>
    </Container>
  );
}

// Fills its container rather than growing with its content, so the message can centre and the actions
// can sit at the foot. `height: 100%` carries the slideout, whose body resolves a height of its own;
// `flex: 1` carries a flex-column caller; and the floor keeps the state looking deliberate on a
// surface that gives it neither — the customer page renders this same component.
const Container = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  height: 100%;
  min-height: 360px;
`;

const Message = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
`;

const Icon = styled.div`
  display: inline-flex;
  margin-bottom: 16px;
`;

// Title, sentence and glyph all sit at black50 — the state is an explanation, not a call to action,
// so it reads as one muted block rather than a heading competing with the form it replaced.
// ChatBubbleIcon already defaults to the same token.
const Title = styled(BodySmallBold)`
  font-size: 16px;
  color: ${colors.black50};
  margin-bottom: 12px;
`;

// Capped so the sentence wraps into a readable measure instead of stretching the full width of a
// panel that is up to 1120px wide.
const Description = styled(BodySmall)`
  color: ${colors.black50};
  max-width: 560px;
`;

// Matches the form's own actions row, so switching between the two states doesn't shift the buttons.
const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 16px;
`;
